import React, { SetStateAction, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Form,
  Row,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IPagination } from "../../../shared/interfaces/shared/IPagination";
import { IInstitution } from "../../../shared/interfaces/institution/IInstitution";
import PageSizeSelect from "../../../shared/components/PageSizeSelect";
import Pagination from "../../../shared/components/Pagination";
import DeleteModal from "../../../shared/components/DeleteModal";
import { enqueueSnackbar } from "notistack";
import { handleError } from "../../../shared/functions/handleError";
import { InstitutionService } from "../service";

interface IProps {
  institutions: IInstitution[];
  pagination: IPagination;
  setPagination: React.Dispatch<SetStateAction<IPagination>>;
  totalPages: number;
  handleUpdateList: () => void;
  changeFilterHandler: (
    name: string,
    value: string | number | boolean | number[] | null,
  ) => void;
}
const List = ({
  institutions,
  pagination,
  setPagination,
  totalPages,
  handleUpdateList,
  changeFilterHandler,
}: IProps) => {
  const { t } = useTranslation();
  const [localPagination, setLocalPagination] = useState<IPagination | null>(
    null,
  );
  const [isDeleteVisible, setIsDeleteVisible] = useState(false);
  const [selectedInstitution, setSelectedInstitution] = useState<
    IInstitution | undefined
  >(undefined);
  const handleCloseDelete = () => {
    setIsDeleteVisible(false);
    setSelectedInstitution(undefined);
  };

  const handleDelete = (id: number) => {
    InstitutionService.delete(id)
      .then(() => {
        handleUpdateList();
        handleCloseDelete();
        enqueueSnackbar(
          t("toasts.successfullyDeleted", {
            name: selectedInstitution?.name,
          }),
          {
            variant: "success",
          },
        );
      })
      .catch(handleError);
  };
  const changeLocalPaginationHandler = (
    name: string,
    value: string | number | boolean | number[] | null,
  ) => {
    setLocalPagination((prev) => ({ ...prev, [name]: value }) as IPagination);
  };
  return (
    <React.Fragment>
      <Card>
        <Card.Header>
          <h5>{t("institution.filterInstitutions")}</h5>
        </Card.Header>
        <CardBody>
          <Row>
            <Col md={3}>
              <Form.Control
                placeholder={t("general.searchByName")}
                name={"name"}
                value={localPagination?.name ?? ""}
                onChange={(e) =>
                  changeLocalPaginationHandler(e.target.name, e.target.value)
                }
                className={"w-100"}
              />
            </Col>
            <Col md={3}>
              <Form.Control
                placeholder={t("general.searchByEmail")}
                value={localPagination?.email}
                name={"email"}
                onChange={(e) =>
                  changeLocalPaginationHandler(e.target.name, e.target.value)
                }
              />
            </Col>
            <Col md={3}>
              <Form.Control
                placeholder={t("general.searchByCity")}
                value={localPagination?.city}
                name={"city"}
                onChange={(e) =>
                  changeLocalPaginationHandler(e.target.name, e.target.value)
                }
              />
            </Col>
            <Col md={3}>
              <Form.Control
                placeholder={t("general.searchByPhone")}
                value={localPagination?.phoneNumber}
                name={"phoneNumber"}
                onChange={(e) =>
                  changeLocalPaginationHandler(e.target.name, e.target.value)
                }
              />
            </Col>
          </Row>
        </CardBody>
        <CardFooter className={"text-end"}>
          <Button
            variant={"light"}
            onClick={() => {
              setLocalPagination(null);
              setPagination({ page: 0, size: 20 });
            }}
          >
            {t("button.reset")}
          </Button>
          <Button
            variant={"primary"}
            className={"ms-3"}
            onClick={() => {
              setPagination((prev) => ({ ...prev, ...localPagination }));
            }}
          >
            {t("button.search")}
          </Button>
        </CardFooter>
      </Card>
      <Card className="table-card">
        <CardHeader>
          <h5>{t("institution.institutionList")}</h5>
        </CardHeader>

        <CardBody className="pt-3">
          <div className="table-responsive">
            <table className="table table-hover" id="pc-dt-simple">
              <thead>
                <tr>
                  <th>{t("general.name")}</th>
                  <th>{t("general.address")}</th>
                  <th>{t("general.city")}</th>
                  <th>{t("general.action")}</th>
                </tr>
              </thead>
              <tbody>
                {institutions.map((institution) => {
                  return (
                    <tr key={institution?.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="flex-shrink-0">
                            {institution?.logo ? (
                              <img
                                src={institution.logo}
                                alt={institution?.name}
                                className="img-radius wid-40"
                              />
                            ) : (
                              <div
                                className="avtar avtar-s bg-light-primary"
                                style={{ width: "40px", height: "40px" }}
                              >
                                {`${institution?.name?.[0] ?? ""}`}
                              </div>
                            )}
                          </div>

                          <div className="flex-grow-1 ms-3">
                            <h6 className="mb-0">{institution?.name}</h6>
                          </div>
                        </div>
                      </td>
                      <td>{institution?.address}</td>
                      <td>{institution?.city}</td>
                      <td>
                        <Link
                          to={`/institutions/${institution?.id}`}
                          className="avtar avtar-xs btn-link-secondary"
                        >
                          <i className="ti ti-edit f-20"></i>
                        </Link>
                        <Link
                          to="#"
                          onClick={() => {
                            setSelectedInstitution(institution);
                            setIsDeleteVisible(true);
                          }}
                          className="avtar avtar-xs btn-link-secondary"
                        >
                          <i className="ti ti-trash f-20"></i>
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <PageSizeSelect
            value={pagination.size}
            onChange={(size) => changeFilterHandler("size", size)}
          />

          <Pagination
            currentPage={pagination.page}
            totalPages={totalPages}
            onPageChange={(page) => changeFilterHandler("page", page)}
          />
        </CardBody>
      </Card>
      <DeleteModal
        show={isDeleteVisible}
        handleClose={handleCloseDelete}
        handleDeleteId={() => handleDelete(Number(selectedInstitution?.id))}
        name={selectedInstitution?.name}
      />
    </React.Fragment>
  );
};

export default List;
